import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState, type ReactNode } from 'react'

import { authApi } from '@/api/endpoints'

import { AuthContext } from './auth-context'
import { clearStoredSession, getStoredSession, storeSession, type StoredSession } from './session'

/**
 * Stato della sessione, letto sincronicamente da `localStorage` all'avvio —
 * come il tema, così non c'è un frame di contenuto protetto mostrato e poi
 * ritirato.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(getStoredSession)
  const queryClient = useQueryClient()

  const login = useCallback(
    async (input: { username: string; password: string }) => {
      const response = await authApi.login(input)
      const next: StoredSession = { token: response.token, username: response.user.username }
      storeSession(next)
      setSession(next)
      // Dati in cache appartengono a chi era dentro prima: con un backend vero
      // sarebbero di un altro utente.
      queryClient.clear()
    },
    [queryClient],
  )

  const logout = useCallback(() => {
    clearStoredSession()
    queryClient.clear()
    setSession(null)
    // Il logout locale è già avvenuto: se il server non risponde non c'è
    // nulla da annullare lato client.
    void authApi.logout().catch(() => {})
  }, [queryClient])

  const value = useMemo(() => ({ session, login, logout }), [session, login, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
